// Database types for the Automation Recipe Library

export type ToolCategory = 
  | 'accounting'
  | 'payments'
  | 'crm'
  | 'marketing'
  | 'ecommerce'
  | 'productivity'
  | 'communication'
  | 'analytics'
  | 'hr'
  | 'automation'
  | 'other';

export type ConnectorType = 'make' | 'zapier' | 'n8n' | 'both';

export type RecipeStatus = 'draft' | 'published' | 'flagged';

export type SourceType = 'api' | 'docs' | 'forum' | 'video' | 'other';

export type AppRole = 'admin';

export interface Tool {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  category: ToolCategory;
  logo_url: string | null;
  website_url: string | null;
  api_docs_url: string | null;
  affiliate_program_url: string | null;
  affiliate_link: string | null;
  is_active: boolean;
  last_verified_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Recipe {
  id: string;
  title: string;
  slug: string;
  subtitle: string | null;
  tool_a_id: string | null;
  tool_b_id: string | null;
  connector: ConnectorType;
  status: RecipeStatus;
  problem: string | null;
  outcome: string | null;
  automation_map: string | null;
  steps: string | null;
  data_mapping: string | null;
  gotchas: string | null;
  verification_checklist: string | null;
  cta_primary_label: string | null;
  cta_primary_url: string | null;
  cta_secondary_label: string | null;
  cta_secondary_url: string | null;
  confidence_score: number | null;
  last_verified_at: string | null;
  view_count: number;
  upvote_count: number;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
  // Joined data
  tool_a?: Tool;
  tool_b?: Tool;
}

export interface Source {
  id: string;
  recipe_id: string;
  source_type: SourceType;
  title: string;
  url: string | null;
  key_claims: string | null;
  captured_at: string;
  created_at: string;
}

export interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
  created_at: string;
}

// Form types
export interface ToolFormData {
  name: string;
  slug: string;
  description: string;
  category: ToolCategory;
  logo_url: string;
  website_url: string;
  api_docs_url: string;
  affiliate_program_url: string;
  affiliate_link: string;
  is_active: boolean;
}

export interface RecipeFormData {
  title: string;
  slug: string;
  subtitle: string;
  tool_a_id: string;
  tool_b_id: string;
  connector: ConnectorType;
  status: RecipeStatus;
  problem: string;
  outcome: string;
  automation_map: string;
  steps: string;
  data_mapping: string;
  gotchas: string;
  verification_checklist: string;
  cta_primary_label: string;
  cta_primary_url: string;
  cta_secondary_label: string;
  cta_secondary_url: string;
  confidence_score: number;
  is_featured: boolean;
}

export interface SourceFormData {
  recipe_id: string;
  source_type: SourceType;
  title: string;
  url: string;
  key_claims: string;
}

// Category display names
export const CATEGORY_LABELS: Record<ToolCategory, string> = {
  accounting: 'Accounting',
  payments: 'Payments',
  crm: 'CRM',
  marketing: 'Marketing',
  ecommerce: 'E-commerce',
  productivity: 'Productivity',
  communication: 'Communication',
  analytics: 'Analytics',
  hr: 'HR',
  automation: 'Automation',
  other: 'Other',
};

export const CONNECTOR_LABELS: Record<ConnectorType, string> = {
  make: 'Make',
  zapier: 'Zapier',
  n8n: 'n8n',
  both: 'Make & Zapier',
};

// Affiliate links for automation platforms
export const CONNECTOR_AFFILIATE_LINKS: Record<ConnectorType, { url: string; label: string }> = {
  make: { url: 'https://www.make.com/en/register?pc=lovable', label: 'Try Make Free' },
  zapier: { url: 'https://zapier.com/sign-up?utm_source=lovable', label: 'Try Zapier Free' },
  n8n: { url: 'https://app.n8n.cloud/register?ref=lovable', label: 'Try n8n Free' },
  both: { url: 'https://www.make.com/en/register?pc=lovable', label: 'Try Make Free' },
};

export const STATUS_LABELS: Record<RecipeStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  flagged: 'Needs Review',
};

export const SOURCE_TYPE_LABELS: Record<SourceType, string> = {
  api: 'API Documentation',
  docs: 'Official Docs',
  forum: 'Community Forum',
  video: 'Video Tutorial',
  other: 'Other',
};
